<script setup lang="ts">
import { inBrowser, useData } from "vitepress";
import { defineAsyncComponent, nextTick, onMounted, provide, ref } from "vue";
import MyContent from "../components/MyContent.vue";
import MyFooter from "../footer/MyFooter.vue";
import MyNav from "../navigation/MyNav.vue";
import MyNavScreen from "../navigation/MyNavScreen.vue";

// Delay Three.js load until after page is idle to avoid "unused JS" in PageSpeed
const showBackground = ref(false);
const DitherShapeBackground = defineAsyncComponent(
  () => import("../background/DitherShapeBackground.vue"),
);

onMounted(() => {
  // Wait for page load, then idle callback, then load Three.js
  const load = () => {
    if ("requestIdleCallback" in window) {
      requestIdleCallback(() => {
        showBackground.value = true;
      });
    } else {
      setTimeout(() => {
        showBackground.value = true;
      }, 200);
    }
  };

  if (document.readyState === "complete") {
    load();
  } else {
    window.addEventListener("load", load, { once: true });
  }
});

const { isDark } = useData();

const enableTransitions = () =>
  inBrowser &&
  "startViewTransition" in document &&
  window.matchMedia("(prefers-reduced-motion: no-preference)").matches;

provide("toggle-appearance", async ({ clientX: x, clientY: y }: MouseEvent) => {
  if (!enableTransitions()) {
    isDark.value = !isDark.value;
    return;
  }

  const clipPath = [
    `circle(0px at ${x}px ${y}px)`,
    `circle(${Math.hypot(
      Math.max(x, innerWidth - x),
      Math.max(y, innerHeight - y),
    )}px at ${x}px ${y}px)`,
  ];

  await document.startViewTransition(async () => {
    isDark.value = !isDark.value;
    await nextTick();
  }).ready;

  document.documentElement.animate(
    { clipPath: isDark.value ? clipPath.reverse() : clipPath },
    {
      duration: 300,
      easing: "ease-in",
      fill: !isDark.value ? "forwards" : "backwards",
      pseudoElement: `::view-transition-${isDark.value ? "old" : "new"}(root)`,
    },
  );
});
</script>

<template>
  <ClientOnly>
    <DitherShapeBackground v-if="showBackground" />
  </ClientOnly>
  <MyNav />
  <MyNavScreen />
  <MyContent />
  <MyFooter />
</template>

<style lang="scss" scoped>
/* View transition animations for theme toggle */
::view-transition-old(root),
::view-transition-new(root) {
  animation: none;
  mix-blend-mode: normal;
}

::view-transition-old(root),
.dark::view-transition-new(root) {
  z-index: 1;
}

::view-transition-new(root),
.dark::view-transition-old(root) {
  z-index: 9999;
}
</style>
